import { fmtCurrency, fmtDate, maskCNPJ, parseLocalDate } from './formatters';

/**
 * O que cada seção FECHADA do modal de proposta diz sobre si mesma.
 *
 * Colapsar só é seguro enquanto a linha de resumo não mente. Se ela disser
 * "R$ 0,00/mês" numa proposta que tem valor, ou omitir que a validade já
 * venceu, o vendedor decide sobre a seção fechada com a informação errada e
 * nada na tela o corrige — por isso a conta mora aqui, com teste, e não dentro
 * do JSX.
 */

export interface Investimento {
  /** Recorrente por mês, já com o desconto por volume aplicado. */
  mensal: number;
  /** Cobrança única (P&S), já com o desconto aplicado. */
  unico: number;
}

/**
 * Mensal e único NÃO se somam: são naturezas diferentes de cobrança. O desconto
 * incide igual nos dois, como no bloco Investimento do modal — esta é a única
 * definição, e o bloco passou a usá-la para os dois números nunca discordarem.
 */
export function investimentoFinal(
  totalMRR: number,
  totalPS: number,
  descontoPercent: number,
): Investimento {
  const d = 1 - (Number(descontoPercent) || 0) / 100;
  return { mensal: (Number(totalMRR) || 0) * d, unico: (Number(totalPS) || 0) * d };
}

export function resumoDoInvestimento(i: Investimento): string {
  const partes: string[] = [];
  if (i.mensal > 0) partes.push(`${fmtCurrency(i.mensal)}/mês`);
  if (i.unico > 0) partes.push(`+ ${fmtCurrency(i.unico)} único`);
  return partes.length ? partes.join(' ') : fmtCurrency(0);
}

export function resumoDosItens(quantidade: number, i: Investimento): string {
  const n = Number(quantidade) || 0;
  if (n === 0) return 'nenhum item — a proposta sai vazia';
  return `${n === 1 ? '1 item' : `${n} itens`} · ${resumoDoInvestimento(i)}`;
}

export function resumoDaEmpresa(p: {
  clientTradeName?: string | null;
  clientName?: string | null;
  clientCnpj?: string | null;
}): string {
  const nome = String(p.clientTradeName || p.clientName || '').trim();
  const cnpj = String(p.clientCnpj || '').replace(/\D/g, '');
  const partes: string[] = [];
  if (nome) partes.push(nome);
  if (cnpj) partes.push(maskCNPJ(cnpj));
  return partes.length ? partes.join(' · ') : 'sem razão social e sem CNPJ';
}

/**
 * `hoje` é parâmetro para o teste poder fixar a data; em produção ninguém passa.
 * A validade vencida é dita no resumo de propósito: é o campo que envelhece
 * sozinho, e uma seção fechada nunca avisaria por conta própria.
 */
export function resumoDasCondicoes(
  p: {
    validUntil?: string | null;
    installments?: number | null;
    paymentMethod?: string | null;
    contractMonths?: number | null;
  },
  hoje: Date = new Date(),
): string {
  const partes: string[] = [];
  if (p.validUntil) {
    const limite = parseLocalDate(String(p.validUntil));
    const inicioDeHoje = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate());
    const vencida = limite.getTime() < inicioDeHoje.getTime();
    partes.push(`válida até ${fmtDate(p.validUntil)}${vencida ? ' (vencida)' : ''}`);
  } else {
    partes.push('sem validade');
  }
  const parcelas = Number(p.installments) || 1;
  if (parcelas > 1) partes.push(`P&S em ${parcelas}x`);
  if (p.paymentMethod) partes.push(String(p.paymentMethod));
  const meses = Number(p.contractMonths) || 0;
  if (meses > 0) partes.push(`${meses} ${meses === 1 ? 'mês' : 'meses'}`);
  return partes.join(' · ');
}

export function resumoDasObservacoes(notes?: string | null, limite = 80): string {
  const texto = String(notes ?? '').trim().replace(/\s+/g, ' ');
  if (!texto) return 'nenhuma';
  return texto.length <= limite ? texto : `${texto.slice(0, limite - 1).trimEnd()}…`;
}
